import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClickedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowNode,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  ReplaySubject,
  switchMap,
  take,
} from 'rxjs';

import { BudgetService } from '@features/budget/services/budget.service';
import { VendorWorkflowDirective } from '@features/feature-workflow/directives/vendor-workflow.directive';

import { BudgetQuery } from '@models/budget/budget.query';

import { AgCellWrapperComponent } from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import { AgCheckRerenderComponent } from '@shared/ag-components/ag-check-rerender/ag-check-rerender.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { PaginationPanelComponent } from '@shared/components/pagination-panel/pagination-panel.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import {
  DEFAULT_PAGINATION_SIZE,
  initialPaginationSize,
  setPaginationSize,
} from '@shared/constants/pagination.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  BudgetType,
  Currency,
  OrganizationType,
  PermissionType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationModel } from '@shared/store/organization/organization.store';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { CurrencyToggle } from '@shared/types/components.type';
import { injectExecutableByRoute } from '@shared/utils/route.utils';
import { Utils } from '@shared/utils/utils';

import { MultipleOrganizationsDialogComponent } from './multiple-organizations-dialog/multiple-organizations-dialog';
import {
  OrganizationDialogComponent,
  OrganizationDialogData,
} from './organization-dialog/organization-dialog.component';
import { VendorGridActionComponent } from './vendor-grid-action/vendor-grid-action.component';
import { VendorGridOverlayNoRowCellComponent } from './vendor-grid-overlay-no-row-template/vendor-grid-overlay-no-row-cell/vendor-grid-overlay-no-row-cell.component';

export type VendorsGridData = {
  contractCurrency: Currency;
  hasInvoices: boolean;
  hasPurchaseOrders: boolean;
  receivesVendorEstimate: boolean;
  total_budget: number;
  vendor_id: string;
  vendor_name: string;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [{ directive: VendorWorkflowDirective }],
  imports: [
    NgTemplateOutlet,
    ButtonComponent,
    TooltipDirective,
    AgGridAngular,
    StickyGridDirective,
    PaginationPanelComponent,
    AsyncPipe,
  ],
  selector: 'aux-vendors',
  styleUrls: ['./vendors.component.scss'],
  templateUrl: './vendors.component.html',
})
export class VendorsComponent implements AfterViewInit {
  budgetQuery = inject(BudgetQuery);
  ctx = inject(VendorWorkflowDirective);
  vendorsQuery = inject(OrganizationQuery);

  private budgetService = inject(BudgetService);
  private injector = inject(Injector);
  private organizationService = inject(OrganizationService);
  private overlayService = inject(OverlayService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private workflowService = inject(WorkflowService);

  loading = signal(true);

  currentOpenMonth = '';
  currentQuarterMonths: string[] = [];
  gridAPI!: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  gridData$ = new BehaviorSubject<undefined | VendorsGridData[]>(undefined);

  onAddMultipleVendors = () => {
    this.overlayService.openPopup({
      content: MultipleOrganizationsDialogComponent,
      settings: {
        header: 'Add Multiple Vendor Contracts',
        primaryButton: {
          action: async (instance: MultipleOrganizationsDialogComponent | null) => {
            await instance?.addVendors();
          },
          label: 'Add Vendor Contracts',
        },
      },
    });
  };

  onNewVendor = () => {
    this.overlayService.openPopup<OrganizationDialogData, unknown, OrganizationDialogComponent>({
      content: OrganizationDialogComponent,
      data: {
        fetchFilesOnInit: false,
        userHasPermission: true,
      },
      settings: {
        header: 'Add New Vendor Contract',
        primaryButton: {
          action: async (instance) => {
            await instance?.onSubmit();
          },
          disabled: (instance) => !instance?.userHasPermission || !instance.hasChanges(),
          label: 'Save Vendor Contract',
          tooltip: (instance) => {
            return (
              !instance?.userHasPermission
                ? instance?.permissionMessage
                : instance?.saveBtnTooltip()
            ) as string;
          },
        },
      },
    });
  };

  gridOptions = {
    cacheBlockSize: 250,
    columnDefs: [
      {
        field: 'vendor_id',
        headerName: 'vendor_id',
        hide: true,
      },
      {
        cellClass: ['grid-cell', 'action-cell'],
        cellRenderer: VendorGridActionComponent,
        cellRendererParams: {
          deleteClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            this.clickRemoveVendor(rowNode);
          },
          editClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            this.clickEditButton(rowNode);
          },
        },
        field: 'actions',
        headerName: '',
        maxWidth: 85,
        minWidth: 85,
        resizable: false,
      },
      {
        cellClass: [
          'grid-cell',
          'ag-cell-align-left',
          'aux-link',
          'cursor-pointer',
          'truncate',
          '!block',
        ],
        field: 'vendor_name',
        headerName: 'Vendor Contract',
        onCellClicked: (event: CellClickedEvent) => {
          this.clickEditButton(event.node as RowNode);
        },
        resizable: true,
        tooltipField: 'vendor_name',
      },
      {
        cellClass: ['grid-cell', 'ag-cell-align-right'],
        field: 'total_budget',
        headerName: 'Total Budget',
        minWidth: 150,
        resizable: true,
        suppressSizeToFit: true,
        valueFormatter: (params: ValueFormatterParams) => {
          return Utils.agMultipleCurrencyFormatter(
            new BehaviorSubject<CurrencyToggle>(CurrencyToggle.CONTRACTED)
          )(params);
        },
        width: 150,
      },
      {
        cellClass: ['grid-cell', 'ag-cell-align-left'],
        field: 'contractCurrency',
        headerName: 'Currency',
        minWidth: 100,
        resizable: true,
        suppressSizeToFit: true,
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value;
        },
        width: 100,
      },
      {
        cellClass: [
          'grid-cell',
          TableConstants.STYLE_CLASSES.CELL_VERTICAL_HORIZONTAL_ALIGN_CENTER,
        ],
        cellRenderer: AgCheckRerenderComponent,
        field: 'receivesVendorEstimate',
        headerName: 'Provides Vendor Estimates',
        suppressSizeToFit: true,
        width: 170,
      },
    ],
    defaultColDef: {
      cellRenderer: AgCellWrapperComponent,
      minWidth: 80,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
    },
    getRowId: ({ data }) => data.vendor_id,
    noRowsOverlayComponent: VendorGridOverlayNoRowCellComponent,
    noRowsOverlayComponentParams: {
      onAddMultipleVendorsFN: () => {
        this.onAddMultipleVendors();
      },
      onNewVendorFN: () => {
        this.onNewVendor();
      },
    },
    pagination: true,
    paginationPageSize: initialPaginationSize(LocalStorageKey.PAGINATION_VENDOR_CONTRACTS),
    suppressCellFocus: true,
    suppressPaginationPanel: true,
    tooltipShowDelay: 0,
  } as GridOptions;
  PermissionType = PermissionType;

  ngAfterViewInit(): void {
    runInInjectionContext(this.injector, () => {
      injectExecutableByRoute(() => this.onNewVendor(), this.ctx.disabledUI);
    });
  }

  clickEditButton(rowNode: RowNode) {
    const foundOrg = this.vendorsQuery.getAll().find((org) => org.id === rowNode.data.vendor_id);

    if (foundOrg) {
      this.onEdit(foundOrg);
    }
  }

  clickRemoveVendor(rowNode: RowNode) {
    const foundOrg = this.vendorsQuery.getAll().find((org) => org.id === rowNode.data.vendor_id);

    if (foundOrg) {
      void this.removeVendor(foundOrg);
    }
  }

  constructor() {
    this.vendorsQuery
      .selectAll()
      .pipe(takeUntilDestroyed())
      .subscribe((vendors) => {
        const vendorGridData: VendorsGridData[] = vendors
          .map((vendor) => {
            if (vendor.organization_type === OrganizationType.ORGANIZATION_VENDOR) {
              return {
                contractCurrency: vendor.currency,
                hasInvoices: vendor.has_invoices,
                hasPurchaseOrders: vendor.has_purchase_orders,
                receivesVendorEstimate: !!vendor.receives_vendor_estimate,
                total_budget: this.vendorsQuery.getPrimaryBudgetVersion(vendor.id)
                  ?.total_budget_amount,
                vendor_id: vendor.id,
                vendor_name: vendor.name,
              } as VendorsGridData;
            }
          })
          .filter((val) => !!val) as VendorsGridData[];

        this.gridData$.next(vendorGridData);
      });

    // this is a hack to wait for our global vendor call to finish
    // after that call finishes; we can call our vendor call
    // this will only happen if the user refreshes the page in this route
    this.vendorsQuery
      .selectLoading()
      .pipe(
        takeUntilDestroyed(),
        filter((loading) => loading === false),
        take(1),
        switchMap(() =>
          this.organizationService.getListWithTotalBudgetAmount(BudgetType.BUDGET_PRIMARY)
        )
      )
      .subscribe(() => {
        this.loading.set(false);
      });

    combineLatest([this.route.queryParams, this.vendorsQuery.selectLoading()])
      .pipe(takeUntilDestroyed())
      .subscribe(([params, loading]) => {
        if (loading) {
          return;
        }

        if (params['selectedVendor']) {
          const org = this.vendorsQuery.getEntity(params['selectedVendor']);

          if (org) {
            this.onEdit(org);
          }

          void this.router.navigate([], { queryParams: {}, replaceUrl: true });
        }
      });

    this.workflowService.getWorkflowList().pipe(takeUntilDestroyed()).subscribe();

    this.budgetService
      .getInMonthBudgetData(null)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        this.currentQuarterMonths =
          state.header_data?.filter((x) => x.group_name === 'Forecast')[0]?.date_headers || [];
      });
  }

  onEdit(org: OrganizationModel) {
    this.overlayService.openPopup<OrganizationDialogData, unknown, OrganizationDialogComponent>({
      content: OrganizationDialogComponent,
      data: {
        currentQuarterMonths: this.currentQuarterMonths,
        fetchFilesOnInit: true,
        id: org.id,
        userHasPermission: this.ctx.userHasVendorPermission(),
      },
      settings: {
        header: 'Vendor Contract',
        primaryButton: {
          action: async (instance: null | OrganizationDialogComponent) => {
            await instance?.onSubmit();
          },
          disabled: (instance) => !instance?.userHasPermission || !instance.hasChanges(),
          label: 'Save Edits',
          tooltip: (instance) => {
            return (
              !instance?.userHasPermission
                ? instance?.permissionMessage
                : instance?.saveBtnTooltip()
            ) as string;
          },
        },
      },
    });
  }

  onFirstDataRendered() {
    this.gridAPI.sizeColumnsToFit();
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.gridAPI$.next(api);
    this.gridAPI.sizeColumnsToFit();
  }

  onPaginationChange(): void {
    setPaginationSize(
      LocalStorageKey.PAGINATION_VENDOR_CONTRACTS,
      this.gridAPI?.paginationGetPageSize() || DEFAULT_PAGINATION_SIZE
    );
  }

  async removeVendor(vendor: OrganizationModel) {
    if (!vendor) return;

    const modalEvent = this.overlayService.openPopup<ConfirmationActionModalData>({
      data: {
        keywordToExecuteAction: 'Delete Vendor Contract',
        message:
          'This will permanently delete the selected Vendor Contract. This action cannot be undone and will remove all associated data including budget and accruals.',
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Delete Vendor Contract?',
        primaryButton: {
          label: 'Delete Vendor Contract',
          variant: 'negative',
        },
      },
    }).afterClosed$;

    const resp = await firstValueFrom(modalEvent);

    if (resp?.data) {
      await this.organizationService.remove(vendor);
    }
  }

  sizeColumnsToFit(): void {
    this.gridAPI?.sizeColumnsToFit();
  }

  updateGridLayout(): void {
    if (!this.gridAPI) return;

    Utils.updateGridLayout(this.gridAPI, 'VendorsGrid', true);
  }
}
